import BigNumber from 'bignumber.js';
import { ethers } from 'ethers';
import { Options } from '@/utils/layerzero-options';

import { METIS_MAINNET_EID } from '../constants';
import { useGetContractByUserWeb3 } from '@/hooks/use-contract';
import OFT_ABI from '@/contracts/goatLayerzeroAdaper/abi.json';
import user from '@/stores/user';
import ModalFunc from '@/utils/components/modal';
import Alert from '@/utils/components/alert';
import useTokenBalance from './use-token-balance';
import { handleError } from '@/utils/error-handler';

interface SendParam {
    dstEid: number;
    to: string;
    amountLD: string;
    minAmountLD: string;
    extraOptions: string;
    composeMsg: string;
    oftCmd: string;
}

interface MessagingFee {
    nativeFee: bigint;
    lzTokenFee: bigint;
}

interface EstimateResult {
    fee: MessagingFee;
    feeInEther: string;
    nativeFee: bigint;
}

interface OFTSendParams {
    tokenAddress: string;
    decimals: number;
    amount: BigNumber;
    recipientAddress: string;
    dstEid?: number;
    gasLimit?: number;
    slippage?: number;
}

/**
 * Hook for GOAT network OFT tokens
 * OFT tokens don't need approval since they have native cross-chain functionality
 */
export default function useGoatTokenOFT() {
    const getContract = useGetContractByUserWeb3();
    const { chainId } = user;

    // Get native token balance for gas estimation
    const { balance: nativeBalance } = useTokenBalance('');

    // Estimate gas and fees for cross-chain transfer
    async function estimateOFTFee(
        params: OFTSendParams,
    ): Promise<EstimateResult | undefined> {
        const {
            tokenAddress,
            decimals,
            amount,
            recipientAddress,
            dstEid = METIS_MAINNET_EID,
            gasLimit = 200000,
        } = params;

        if (!user.address || !chainId) {
            console.error('Wallet not connected');
            return undefined;
        }

        if (!tokenAddress) {
            console.error('Token address not provided');
            return undefined;
        }

        try {
            // Use token address directly as OFT contract
            const oftContract = await getContract(tokenAddress, OFT_ABI, false);

            if (!oftContract) {
                return undefined;
            }

            // Build send parameters
            const amountLD = amount
                .shiftedBy(decimals)
                .integerValue()
                .toFixed();

            const slippage = params.slippage || 0; // Default 0% slippage
            const minAmountLD = amount
                .multipliedBy(1 - slippage)
                .shiftedBy(decimals)
                .integerValue()
                .toFixed();

            // Create extra options
            const extraOptions = Options.newOptions()
                .addExecutorLzReceiveOption(gasLimit, 0)
                .toHex();

            const sendParam: SendParam = {
                dstEid: dstEid,
                to: ethers.zeroPadValue(recipientAddress, 32),
                amountLD: amountLD,
                minAmountLD: minAmountLD,
                extraOptions: '0x',
                composeMsg: '0x',
                oftCmd: '0x',
            };

            console.log(
                'Estimating OFT LayerZero fees with params:',
                sendParam,
            );

            // Quote the fee (use native token, not LZ token)
            const fee = await oftContract.quoteSend(sendParam, false);

            console.log('Fee estimate:', {
                nativeFee: fee.nativeFee.toString(),
                lzTokenFee: fee.lzTokenFee.toString(),
            });

            const feeInEther = new BigNumber(fee.nativeFee.toString())
                .shiftedBy(-18)
                .toFixed();

            return {
                fee: {
                    nativeFee: BigInt(fee.nativeFee.toString()),
                    lzTokenFee: BigInt(fee.lzTokenFee.toString()),
                },
                feeInEther: feeInEther,
                nativeFee: BigInt(fee.nativeFee.toString()),
            };
        } catch (e) {
            console.error('Estimate OFT gas error:', e);
            handleError(e);
            return undefined;
        }
    }

    // Send cross-chain transfer using OFT token
    // No approval needed since we're calling the token's own send method
    async function sendOFTToMetis(
        params: OFTSendParams,
    ): Promise<string | false> {
        const {
            tokenAddress,
            decimals,
            amount,
            recipientAddress,
            dstEid = METIS_MAINNET_EID,
            gasLimit = 200000,
        } = params;

        if (!user.address || !chainId) {
            Alert({
                type: 'error',
                text: 'Wallet not connected',
            });
            return false;
        }

        if (!tokenAddress) {
            Alert({
                type: 'error',
                text: 'Token address not provided',
            });
            return false;
        }

        try {
            // First, estimate the fee
            const estimate = await estimateOFTFee(params);

            if (!estimate) {
                Alert({
                    type: 'error',
                    text: 'Failed to estimate gas',
                });
                return false;
            }

            // Check if user has enough native token for fee
            const feeInNative = new BigNumber(estimate.feeInEther);
            if (feeInNative.isGreaterThan(nativeBalance)) {
                ModalFunc({
                    type: 'sorry',
                    title: 'Sorry',
                    text: `Insufficient native token balance. Need ${feeInNative.toFixed(
                        6,
                    )} for gas, but have ${nativeBalance.toFixed(6)}`,
                    buttonText: 'OK',
                });
                return false;
            }

            // Use token address directly as OFT contract
            const oftContract = await getContract(tokenAddress, OFT_ABI, true);

            if (!oftContract) {
                return false;
            }

            // Build send parameters
            const amountLD = amount
                .shiftedBy(decimals)
                .integerValue()
                .toFixed();

            const slippage = params.slippage || 0.01;
            const minAmountLD = amount
                .multipliedBy(1 - slippage)
                .shiftedBy(decimals)
                .integerValue()
                .toFixed();

            const extraOptions = Options.newOptions()
                .addExecutorLzReceiveOption(gasLimit, 0)
                .toHex();

            const sendParam: SendParam = {
                dstEid: dstEid,
                to: ethers.zeroPadValue(recipientAddress, 32),
                amountLD: amountLD,
                minAmountLD: minAmountLD,
                extraOptions: '0x',
                composeMsg: '0x',
                oftCmd: '0x',
            };

            console.log('Sending OFT cross-chain transaction...', sendParam);

            // Send transaction (pay with native token)
            // No approval needed - calling token's own send method
            const tx = await oftContract.send(
                sendParam,
                estimate.fee,
                user.address, // refund address
                {
                    value: estimate.nativeFee.toString(),
                },
            );

            const receipt = await tx.wait();

            if (receipt && receipt.hash) {
                console.log('Transaction confirmed:', receipt.hash);

                return receipt.hash;
            } else {
                return false;
            }
        } catch (e) {
            console.error('Send OFT to Metis error:', e);
            handleError(e);
            return false;
        }
    }

    return {
        // Methods
        estimateOFTFee,
        sendOFTToMetis,
    };
}
